import type { DerbyRngV1 } from '../rng';
import type { StatId } from '../race/types';
import type { TraitId } from './traits';
import { TRAIT_DEFS, TRAIT_IDS } from './traits';

/**
 * Two-parent genetics on the game's 1–20 stat scale. The hidden-allele
 * gamble is the whole game: a 19/9 horse and a 15/15 horse both express
 * ~15, but they breed very differently. Trained stats NEVER pass down —
 * potentials do, so every generation must still be raised.
 */

export interface StatGenes {
  a: number;
  b: number;
}

/** Incomplete dominance: the strong allele carries more weight. */
export function expression(genes: StatGenes): number {
  return Math.max(
    1,
    Math.min(20, Math.round(0.6 * Math.max(genes.a, genes.b) + 0.4 * Math.min(genes.a, genes.b))),
  );
}

/** Coat loci, alleles as counts of the dominant/dilute variant (0|1 per
 * copy). A simplified slice of real horse color genetics. */
export interface CoatGenes {
  /** E: black pigment possible; ee = red (chestnut) base. */
  extension: [number, number];
  /** A: restricts black to points (bay); aa on E_ = black. */
  agouti: [number, number];
  /** G: dominant gray, overrides everything. */
  gray: [number, number];
  /** CR: incomplete-dominant cream dilution. */
  cream: [number, number];
  /** Socks/blaze tier, blended inheritance. */
  markings: number;
}

export type CoatPhenotype =
  | 'gray'
  | 'bay'
  | 'black'
  | 'chestnut'
  | 'palomino'
  | 'buckskin'
  | 'cremello';

export function coatPhenotype(coat: CoatGenes): CoatPhenotype {
  if (coat.gray[0] + coat.gray[1] > 0) return 'gray';
  const creams = (coat.cream[0] > 0 ? 1 : 0) + (coat.cream[1] > 0 ? 1 : 0);
  const blackCapable = coat.extension[0] + coat.extension[1] > 0;
  if (!blackCapable) {
    // Red base.
    if (creams === 0) return 'chestnut';
    return creams === 1 ? 'palomino' : 'cremello';
  }
  const bay = coat.agouti[0] + coat.agouti[1] > 0;
  if (bay) {
    if (creams === 0) return 'bay';
    return creams === 1 ? 'buckskin' : 'cremello';
  }
  // Black base; single cream barely shows ("smoky black").
  return creams >= 2 ? 'cremello' : 'black';
}

export interface Genotype {
  stats: Record<StatId, StatGenes>;
  /** Per trait locus: presence of the allele on each copy. */
  traits: Record<TraitId, [number, number]>;
  coat: CoatGenes;
}

export const STAT_IDS: readonly StatId[] = ['speed', 'stamina', 'accel', 'heart'];

export function expressedPotential(genotype: Genotype): Record<StatId, number> {
  const out = {} as Record<StatId, number>;
  for (const stat of STAT_IDS) out[stat] = expression(genotype.stats[stat]);
  return out;
}

/** Mutation: mostly a faithful copy, sometimes a nudge, rarely a jump. */
function mutate(allele: number, rng: DerbyRngV1): number {
  const roll = rng.next();
  if (roll < 0.85) return allele;
  const magnitude = roll < 0.97 ? rng.integer(-1, 1) : rng.integer(-2, 2);
  return Math.max(1, Math.min(20, allele + magnitude));
}

export function inheritStatGenes(sire: StatGenes, dam: StatGenes, rng: DerbyRngV1): StatGenes {
  const fromSire = rng.chance(0.5) ? sire.a : sire.b;
  const fromDam = rng.chance(0.5) ? dam.a : dam.b;
  return { a: mutate(fromSire, rng), b: mutate(fromDam, rng) };
}

function inheritCoat(sire: CoatGenes, dam: CoatGenes, rng: DerbyRngV1): CoatGenes {
  const pick = (pair: [number, number]): number => (rng.chance(0.5) ? pair[0] : pair[1]);
  return {
    extension: [pick(sire.extension), pick(dam.extension)],
    agouti: [pick(sire.agouti), pick(dam.agouti)],
    gray: [pick(sire.gray), pick(dam.gray)],
    cream: [pick(sire.cream), pick(dam.cream)],
    markings: Math.max(
      0,
      Math.min(3, Math.round((sire.markings + dam.markings) / 2) + rng.integer(-1, 1)),
    ),
  };
}

export function inheritGenotype(sire: Genotype, dam: Genotype, rng: DerbyRngV1): Genotype {
  const stats = {} as Record<StatId, StatGenes>;
  for (const stat of STAT_IDS) stats[stat] = inheritStatGenes(sire.stats[stat], dam.stats[stat], rng);
  const traits = {} as Record<TraitId, [number, number]>;
  for (const trait of TRAIT_IDS) {
    const fromSire = rng.chance(0.5) ? sire.traits[trait][0] : sire.traits[trait][1];
    const fromDam = rng.chance(0.5) ? dam.traits[trait][0] : dam.traits[trait][1];
    let pair: [number, number] = [fromSire, fromDam];
    // De novo jackpots on rare loci only.
    if (TRAIT_DEFS[trait].rarity === 'rare' && rng.chance(0.005)) {
      pair = rng.chance(0.5) ? [1, pair[1]] : [pair[0], 1];
    }
    traits[trait] = pair;
  }
  return { stats, traits, coat: inheritCoat(sire.coat, dam.coat, rng) };
}

/** Legacy horses and 4A saves: genes that exactly reproduce the stored
 * potential (a = b), no traits, a coat drawn from the given rng. */
export function foundationGenotype(
  potential: Record<StatId, number>,
  rng: DerbyRngV1,
): Genotype {
  const stats = {} as Record<StatId, StatGenes>;
  for (const stat of STAT_IDS) {
    const value = Math.max(1, Math.min(20, Math.round(potential[stat])));
    stats[stat] = { a: value, b: value };
  }
  const traits = {} as Record<TraitId, [number, number]>;
  for (const trait of TRAIT_IDS) traits[trait] = [0, 0];
  return { stats, traits, coat: randomCoat(rng) };
}

export function randomCoat(rng: DerbyRngV1): CoatGenes {
  return {
    extension: [rng.chance(0.65) ? 1 : 0, rng.chance(0.65) ? 1 : 0],
    agouti: [rng.chance(0.6) ? 1 : 0, rng.chance(0.6) ? 1 : 0],
    gray: [rng.chance(0.06) ? 1 : 0, rng.chance(0.06) ? 1 : 0],
    cream: [rng.chance(0.12) ? 1 : 0, rng.chance(0.12) ? 1 : 0],
    markings: rng.integer(0, 3),
  };
}

const TRAIT_ALLELE_CHANCE = { common: 0.12, uncommon: 0.06, rare: 0.025 } as const;

/** Market and stud stock: alleles around a quality mean, wild types mixed
 * in, traits seeded by rarity. */
export function randomGenotype(rng: DerbyRngV1, mean: number, spread: number): Genotype {
  const stats = {} as Record<StatId, StatGenes>;
  for (const stat of STAT_IDS) {
    stats[stat] = {
      a: Math.max(1, Math.min(20, mean + rng.integer(-spread, spread))),
      b: Math.max(1, Math.min(20, mean + rng.integer(-spread, spread))),
    };
  }
  const traits = {} as Record<TraitId, [number, number]>;
  for (const trait of TRAIT_IDS) {
    const chance = TRAIT_ALLELE_CHANCE[TRAIT_DEFS[trait].rarity];
    traits[trait] = [rng.chance(chance) ? 1 : 0, rng.chance(chance) ? 1 : 0];
  }
  return { stats, traits, coat: randomCoat(rng) };
}

export type Grade = 'S' | 'A' | 'B' | 'C' | 'D';

export function gradeOf(potential: number): Grade {
  if (potential >= 18) return 'S';
  if (potential >= 15) return 'A';
  if (potential >= 12) return 'B';
  if (potential >= 9) return 'C';
  return 'D';
}
